import React, { useEffect, useRef, useState, useMemo } from 'react'
import {
  FocusContext,
  setFocus,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import tts from '../../lib/TTS'
import {
  secondsToTTS,
  platformTransitionStyle,
  throttle,
} from '../../../../shared/helpers'
import VideoSection from './VideoSection'
import Spinner from '../../components/Spinner'

import type { FocusableComponentLayout } from '@noriginmedia/norigin-spatial-navigation'
import type { SeasonContentProps } from './types'

const MARGIN_TOP = 20
const SEASONS_COMPONENT_FOCUS_KEY = 'SEASONS'

const SeasonContent = (props: SeasonContentProps) => {
  const [translateSectionsY, setTranslateSectionsY] = useState(0)
  const [translateSContentY, setTranslateSContentY] = useState(0)
  const listTopOffsetRef = useRef(0)
  const [sectionTitle, setSectionTitle] = useState('')
  const { ref, focusKey, hasFocusedChild } = useFocusable({
    focusKey: 'SEASON_CONTENT',
    onBlur: () => setSectionTitle(''),
    trackChildren: true,
  })

  let titleText = ''
  if (props.activeSeason?.ordinal) {
    if (props.activeSeason.title && props.activeSeason.title.length > 0) {
      titleText = `Season ${props.activeSeason.ordinal}: ${props.activeSeason.title}`
    } else {
      titleText = `Season ${props.activeSeason.ordinal}`
    }
  } else {
    titleText = 'More Extras'
  }

  useEffect(
    () => () => {
      setTranslateSContentY(0)
      setTranslateSectionsY(0)
      listTopOffsetRef.current = 0
    },
    [props.activeSeason?.cid]
  )

  const handleFocus = (layout: FocusableComponentLayout, extraProps) => {
    speak(extraProps)
    const isFirstSectionEmpty = extraProps.firstSectionLength === 0
    const isFirstSection = extraProps.sectionIndex === 0

    if (isFirstSectionEmpty && isFirstSection) {
      return
    }

    if (!listTopOffsetRef.current && extraProps.index === 0) {
      listTopOffsetRef.current = layout.top
    }

    if (extraProps.index > 0) {
      setTranslateSContentY(-(listTopOffsetRef.current - MARGIN_TOP))
    } else {
      setTranslateSContentY(0)
    }

    if ([0, 1].includes(extraProps.index)) {
      setTranslateSectionsY(
        -(
          layout.top -
          extraProps.index * layout.height -
          listTopOffsetRef.current
        )
      )
    } else if (extraProps.index > 1) {
      setTranslateSectionsY(
        -(layout.top - listTopOffsetRef.current - layout.height)
      )
    }
  }

  const speak = (extraProps) => {
    let text = ''
    if (sectionTitle) {
      if (sectionTitle !== extraProps.section.title) {
        text = `${extraProps.section.title} ...`
        setSectionTitle(extraProps.section.title)
      }
    } else {
      setSectionTitle(extraProps.section.title)
    }

    tts.stopRead()

    if (extraProps.video) {
      const { video } = extraProps
      const length =
        props.videoSections[0].videos.length +
        (props.videoSections[1] ? props.videoSections[1].videos.length : 0)
      const season = video.parent.season?.ordinal
        ? `... season ... ${video.parent.season?.ordinal} ...`
        : ''
      const episode = video.parent.ordinal
        ? `... episode ... ${video.parent.ordinal} ...`
        : ''
      text = `${text} Focused on ${extraProps.index + 1} of ${length} ... ${
        video.title
      } ${season} ${episode} ${secondsToTTS(video.duration)} ... ${
        video.description_short
      }`
    } else {
      text = `No ${extraProps.section.title} are currently available for this season.`
    }

    tts.addTextToRead(text)
  }

  const handleWheel = useMemo(
    () =>
      throttle((event: React.WheelEvent<HTMLDivElement>) => {
        const deltaY = event.deltaY
        const scrollStep = 300
        if (deltaY > 0) {
          setTranslateSectionsY((prev) => {
            const totalVideos = props.videoSections.reduce(
              (acc, section) => acc + section.videos.length,
              0
            )
            const estimatedItemHeight = 270
            const sectionHeaderHeight = 100
            const numberOfSections = props.videoSections.length
            const estimatedContentHeight =
              totalVideos * estimatedItemHeight +
              numberOfSections * sectionHeaderHeight
            const viewportHeight = window.innerHeight
            const availableHeight = viewportHeight * 0.6

            if (estimatedContentHeight <= availableHeight) return prev

            const maxScrollDistance = -(
              estimatedContentHeight -
              availableHeight +
              200
            )
            const newTranslateY = prev - scrollStep

            return Math.max(maxScrollDistance, newTranslateY)
          })
        } else if (deltaY < 0) {
          setTranslateSectionsY((prev) => Math.min(0, prev + scrollStep))
        }
      }, 600),
    [props.videoSections]
  )

  useKeyDown(
    KeyTypeEnum.Back,
    () => {
      setFocus(SEASONS_COMPONENT_FOCUS_KEY)
    },
    { stopPropagation: hasFocusedChild }
  )

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className="season-content"
        style={{
          transform: `translateY(${translateSContentY}px)`,
        }}
        onWheel={handleWheel}
      >
        {props.loading ? (
          <Spinner />
        ) : (
          <>
            <h3 className="season-content__title">
              {titleText}
              {props.seasonDescription && (
                <div className="season-content__description">
                  {props.seasonDescription}
                </div>
              )}
            </h3>
            <div className="season-content__container">
              <div
                className="season-content__video-sections"
                style={{
                  transform: `translateY(${translateSectionsY}px)`,
                  transition: platformTransitionStyle(
                    'transform 400ms ease-in-out'
                  ),
                }}
              >
                {props?.videoSections?.map((section, index) => (
                  <VideoSection
                    section={section}
                    key={section.key}
                    onFocus={handleFocus}
                    firstSectionLength={
                      props.videoSections[0].videos.length > 0
                        ? props.videoSections[0].videos.length
                        : 0
                    }
                    sectionIndex={index}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </FocusContext.Provider>
  )
}

export default SeasonContent
